import { IAppProps } from '../../../App';
import { useParams } from 'react-router-dom';
import React, { useEffect, useMemo, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import {
  fetchMyRequestsDetails,
  downloadTicketRequest,
  getAccreditationTaskStatus,
  updateRequest,
} from '../../../services/accreditation-service/accreditation-service';
import { ACCR_REQUEST_STATUS } from '../constant';
import useFetchTableData from '../../../hooks/useFetchTableData';
import {
  Card,
  Iconography,
  Link,
  Typography,
  Button,
} from '@hid-galaxy-ui/galaxy-react';
import PageTitle from '../../../components/PageTitle';
import { getTranslation } from '../../../utils/functions';
import { TypographyVariantEnum } from '@hid-galaxy-ui/galaxy-react/components/Typography/typographyEnum';
import { IAccreditationRequest } from '../IAccreditationRequest';
import PageLoader from '../../../components/PageLoader';
import './RequestDetails.scss';
import { GENDER_ENUM_TRANSLATIONS } from '../../accounts/constant';
import { getTranslatedGenderOptions } from '../../accounts/accounrHelper';
import { getAccrRequestStatusTranslationKey } from '../../../utils/status';
import { getCountryNameByCode, getCountryListOptions } from '../../../constants/countryList';
import { getFormattedDate } from '../../../utils/dateUtils';
import { useHistory } from 'react-router-dom';
import buildPATHS from '../../../utils/paths';
import { downloadFile } from '../../../services/download-file/download-file';
import { NotificationError, NotificationSuccess } from '../../../utils/notification';
import InputText from '../../../components/Inputs/InputText/InputText';
import InputDropdown from '../../../components/Inputs/InputDropdown/InputDropdown';
import InputDatePicker from '../../../components/Inputs/InputDatePicker/InputDatePicker';

// Fields that are always read-only (never editable regardless of status)
const READ_ONLY_KEYS = ['REQUEST_ID', 'EMAIL', 'STATUS'];

// Fields locked when status is APPROVED or PRINTED — only the 3 vehicle fields remain editable
const RESTRICTED_EDIT_KEYS = [
  'FIRST_NAME', 'LAST_NAME', 'MOBILE_NUMBER',
  'DATE_OF_BIRTH', 'COUNTRY_OF_BIRTH', 'GENDER',
];

const FIELD_NAME_MAP: Record<string, string> = {
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  MOBILE_NUMBER: 'mobileNumber',
  COUNTRY_OF_BIRTH: 'countryOfBirth',
};

const RequestDetails = (props: IAppProps) => {
  const { toasts, setToasts } = props;
  const { requestId } = useParams();

  const [accountDetails, setAccountDetails] = useState<IAccreditationRequest | null>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const { data, isLoading } = useFetchTableData({
    apiFunction: fetchMyRequestsDetails,
    initialParams: requestId,
    toasts,
    setToasts,
  });

  const history = useHistory();
  const PATHS = buildPATHS();

  useEffect(() => {
    if (data) {
      setAccountDetails(data);
    }
  }, [data]);

  // Stable initial values for Formik — also used to diff dirty fields before PATCH
  const formikInitialValues = useMemo(() => ({
    firstName: accountDetails?.firstName ?? '',
    lastName: accountDetails?.lastName ?? '',
    mobileNumber: accountDetails?.mobileNumber ?? '',
    dateOfBirth: accountDetails?.dateOfBirth ?? null,
    countryOfBirth: accountDetails?.countryOfBirth ?? '',
    gender: accountDetails?.gender ?? null,
  }), [accountDetails]);

  // APPROVED and PRINTED statuses allow editing only the 3 vehicle fields
  const isRestrictedStatus =
    accountDetails?.status === ACCR_REQUEST_STATUS.APPROVED ||
    accountDetails?.status === ACCR_REQUEST_STATUS.PRINTED;

  const isFieldEditable = (key: string): boolean => {
    if (READ_ONLY_KEYS.includes(key)) return false;
    if (isRestrictedStatus && RESTRICTED_EDIT_KEYS.includes(key)) return false;
    return true;
  };

  const getDetailsObject = () => ({
    REQUEST_ID: accountDetails?.requestId,
    EMAIL: accountDetails?.email,
    STATUS: accountDetails?.status
      ? getTranslation(getAccrRequestStatusTranslationKey(accountDetails.status))
      : '',
    FIRST_NAME: accountDetails?.firstName,
    LAST_NAME: accountDetails?.lastName,
    MOBILE_NUMBER: accountDetails?.mobileNumber,
    DATE_OF_BIRTH: getFormattedDate(accountDetails?.dateOfBirth),
    COUNTRY_OF_BIRTH: getCountryNameByCode(accountDetails?.countryOfBirth),
    GENDER: accountDetails?.gender
      ? getTranslation(GENDER_ENUM_TRANSLATIONS[accountDetails?.gender])
      : '',
  });

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleSave = async (values: Record<string, any>) => {
    // Build a patch with only the fields the user actually changed
    const dirtyFields = Object.keys(values).reduce<Record<string, any>>((acc, key) => {
      if (values[key] !== formikInitialValues[key]) {
        acc[key] = values[key];
      }
      return acc;
    }, {});

    if (Object.keys(dirtyFields).length === 0) {
      // Nothing changed — just close edit mode
      setIsEditMode(false);
      return;
    }

    try {
      setIsSaving(true);
      await updateRequest(requestId, dirtyFields);
      // Optimistically update local state so the view reflects changes immediately
      setAccountDetails((prev) => (prev ? { ...prev, ...dirtyFields } : prev));
      setIsEditMode(false);
      setToasts([...toasts, ...NotificationSuccess('REQUEST_UPDATED_SUCCESS')]);
    } catch (error) {
      setToasts([...toasts, ...NotificationError('AN_ERROR_OCCURED')]);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadTicket = async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      const { taskId } = await downloadTicketRequest(requestId);
      let retries = 0;
      const MAX_RETRIES = 30; // 30 × 600ms = 18 seconds max
      const intervalId = setInterval(async () => {
        try {
          if (retries >= MAX_RETRIES) {
            clearInterval(intervalId);
            setToasts([...toasts, ...NotificationError('AN_ERROR_OCCURED')]);
            setIsDownloading(false);
            return;
          }

          const res = await getAccreditationTaskStatus(taskId);
          if (res.status === 'INPROGRESS') {
            retries++;
            return;
          }

          clearInterval(intervalId);
          // No filename override — downloadFile derives the real name (and extension) from the S3 URL path.
          downloadFile(res.resourceLocation);
          setIsDownloading(false);
        } catch (error) {
          clearInterval(intervalId);
          setToasts([...toasts, ...NotificationError('AN_ERROR_OCCURED')]);
          setIsDownloading(false);
        }
      }, 600);
    } catch (error) {
      setIsDownloading(false);
    }
  };



  const renderViewField = (key: string, displayValue: string | undefined) => (
    <Typography variant={TypographyVariantEnum.BodyShortMarketing}>
      {displayValue}
    </Typography>
  );

  const renderEditField = (key: string) => {
    if (key === 'GENDER') {
      return (
        <Field
          key="gender"
          component={InputDropdown}
          name="gender"
          id="gender"
          options={getTranslatedGenderOptions()}
          required={true}
        />
      );
    }
    if (key === 'DATE_OF_BIRTH') {
      return (
        <Field
          key="dateOfBirth"
          component={InputDatePicker}
          name="dateOfBirth"
          id="dateOfBirth"
          max={new Date()}
        />
      );
    }
    if (key === 'COUNTRY_OF_BIRTH') {
      const countryOptions = getCountryListOptions().map((o) => ({ label: o.label, value: o.key }));
      return (
        <Field
          key="countryOfBirth"
          component={InputDropdown}
          name="countryOfBirth"
          id="countryOfBirth"
          options={countryOptions}
          required={true}
        />
      );
    }
    const name = FIELD_NAME_MAP[key];
    return (
      <Field
        key={name}
        component={InputText}
        name={name}
        id={name}
      />
    );
  };

  const renderRequestDetails = () => {
    const detailsObject = getDetailsObject();

    return (
      <Card>
        <Formik
          enableReinitialize={true}
          initialValues={formikInitialValues}
          onSubmit={handleSave}
        >
          {() => (
            <Form>
              <PageTitle>{getTranslation('REQUEST_DETAILS')}</PageTitle>

              <div className="hid-flex hid-spacing__my-05">
                <Link onClick={() => history.push(PATHS.my_requests)}>
                  <Iconography icon="angleLeft" /> &nbsp;
                  {getTranslation('BACK')}
                </Link>
              </div>

              <div className="hid-layout__mt-05 details-container">
                {Object.entries(detailsObject).map(([key, value]) => (
                  <div key={key} className="details-container__item">
                    <Typography variant={TypographyVariantEnum.H3}>
                      {getTranslation(key)}:
                    </Typography>
                    {isEditMode && isFieldEditable(key)
                      ? renderEditField(key)
                      : renderViewField(key, value as string)}
                  </div>
                ))}
              </div>

              <div className="hid-flex hid-flex-row hid-spacing__mt-07">
                {!isEditMode && (
                  <Button
                    className="hid-spacing__mr-03"
                    label={getTranslation('BUTTON_EDIT')}
                    icon={<Iconography icon="pencil" />}
                    onClick={handleEditClick}
                  />
                )}
                {isEditMode && (
                  <>
                    <Button
                      label={getTranslation('BUTTON_SAVE_CHANGES')}
                      disabled={isSaving}
                      type="submit"
                    />
                    <Button
                      label={getTranslation('CANCEL')}
                      variant="secondary"
                      disabled={isSaving}
                      onClick={handleCancelEdit}
                    />
                  </>
                )}
                {!isEditMode && (
                  accountDetails?.status === ACCR_REQUEST_STATUS.PRINTED ||
                  accountDetails?.status === ACCR_REQUEST_STATUS.APPROVED
                ) && (
                    <Button
                      label={isDownloading ? `${getTranslation('BUTTON_DOWNLOAD')}...` : getTranslation('BUTTON_DOWNLOAD')}
                      icon={<Iconography icon="download" />}
                      disabled={isDownloading}
                      onClick={handleDownloadTicket}
                    />
                  )}
              </div>
            </Form>
          )}
        </Formik>
      </Card>
    );
  };

  return isLoading ? <PageLoader /> : renderRequestDetails();
};

export default RequestDetails;
